import React from 'react';
import { motion } from 'framer-motion';
import ScrollAnimation from './ScrollAnimation';
import reactLogo from '../assets/react.svg';
import jsLogo from '../assets/javascript.svg';
import htmlCssLogo from '../assets/html.svg';
import nodeLogo from '../assets/nodejs.svg';
import pythonLogo from '../assets/python.svg';
import djangoLogo from '../assets/django.svg';

const skills = [
  { name: 'React', logo: reactLogo, color: '#61DAFB' },
  { name: 'JavaScript', logo: jsLogo },
  { name: 'HTML/CSS', logo: htmlCssLogo, color: '#E34F26' },
  { name: 'Node.js', logo: nodeLogo, color: '#339933' },
  { name: 'Python', logo: pythonLogo, color: '#3776AB' },
  { name: 'Django', logo: djangoLogo, color: '#092E20' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12,
    },
  },
};

const About = () => (
  <ScrollAnimation>
    <motion.section
      id="technical"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
    >
      <motion.h2
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        Technical Background
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
        style={{ marginBottom: '2rem' }}
      >
        I’m hands-on across the stack: crafting delightful frontends, building reliable backends, and
        shipping production features with clean architecture. I’m especially interested in AI-powered
        products and mobile-first experiences.
      </motion.p>

      {/* Technical snapshot */}
      <motion.div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.75rem',
          marginBottom: '2.5rem',
        }}
        className="technical-stats-grid"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {[
          { number: 'Full‑stack', label: 'Web + API + DB' },
          { number: 'AI', label: 'Product-minded use' },
          { number: 'Mobile', label: 'iOS + Android' },
          { number: 'Ship', label: 'Iterate fast' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            variants={itemVariants}
            style={{
              background: 'var(--bg-tertiary)',
              padding: '1rem',
              borderRadius: '16px',
              textAlign: 'center',
              border: '1px solid var(--border-glass)',
              transition: 'all 0.3s ease',
              minWidth: 0,
            }}
            whileHover={{
              scale: 1.05,
              borderColor: 'var(--aurora-1)',
              boxShadow: '0 10px 30px rgba(0, 212, 255, 0.2)',
            }}
          >
            <motion.span
              style={{
                display: 'block',
                fontSize: 'clamp(1.1rem, 4vw, 2rem)',
                fontWeight: 700,
                fontFamily: 'Syne, sans-serif',
                background: 'linear-gradient(135deg, var(--aurora-1), var(--aurora-2))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {stat.number}
            </motion.span>
            <span style={{ fontSize: 'clamp(0.7rem, 2.5vw, 0.85rem)', color: 'var(--text-secondary)', display: 'block', marginTop: '0.25rem' }}>
              {stat.label}
            </span>
          </motion.div>
        ))}
      </motion.div>

      <motion.h3
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        Tech Stack
      </motion.h3>

      <motion.div
        className="skills-container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {skills.map((skill, index) => (
          <motion.div
            key={skill.name}
            className="skill-button"
            variants={itemVariants}
            whileHover={{
              scale: 1.08,
              y: -5,
              boxShadow: `0 15px 30px rgba(0, 212, 255, 0.25)`,
            }}
            whileTap={{ scale: 0.95 }}
            style={{
              cursor: 'default',
            }}
          >
            <motion.div
              style={{
                position: 'relative',
                width: 28,
                height: 28,
              }}
            >
              <img
                src={skill.logo}
                alt={`${skill.name} logo`}
                className="skill-logo"
              />
              {/* Glow effect behind logo */}
              <motion.div
                style={{
                  position: 'absolute',
                  inset: -4,
                  background: skill.color || 'rgba(0, 212, 255, 0.9)',
                  borderRadius: '50%',
                  filter: 'blur(12px)',
                  opacity: 0,
                  zIndex: -1,
                }}
                whileHover={{ opacity: 0.4 }}
              />
            </motion.div>
            <span>{skill.name}</span>
          </motion.div>
        ))}
      </motion.div>
    </motion.section>
  </ScrollAnimation>
);

export default About;
